import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Link2, Plus, RefreshCw } from "lucide-react";
import { api } from "../../api";
import { money } from "../../lib/format";
import "./ImportSellerMapping.css";

const emptyForm = (issue) => ({
  action: "LINK_EXISTING", node_id: "", parent_node_id: "",
  seller_name: issue.raw_name || "", apply_historical: false,
});

export const ImportSellerMapping = ({ batchId, onComplete }) => {
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({ sellers: [], parents: [] });
  const [forms, setForms] = useState({});
  const [previews, setPreviews] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [busyStartedAt, setBusyStartedAt] = useState(Date.now());
  const [pollError, setPollError] = useState(false);

  const load = useCallback(async () => {
    const [mappingResponse, optionsResponse] = await Promise.all([
      api.get(`/import/${batchId}/seller-mappings`), api.get("/import/mapping-options"),
    ]);
    setData(mappingResponse.data); setOptions(optionsResponse.data);
    setPollError(false);
    setForms((current) => {
      const next = { ...current };
      mappingResponse.data.issues.forEach((issue) => { if (!next[issue.id]) next[issue.id] = emptyForm(issue); });
      return next;
    });
  }, [batchId]);
  useEffect(() => { load().catch((error) => alert(error.response?.data?.detail || "Eşleştirmeler yüklenemedi")); }, [load]);
  const hasActiveJob = data?.issues?.some((issue) => ["QUEUED", "RUNNING"].includes(issue.job?.status));
  useEffect(() => {
    if (!busyId && !hasActiveJob) return undefined;
    setBusyStartedAt(Date.now());
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [busyId, hasActiveJob]);
  useEffect(() => {
    if (!hasActiveJob) return undefined;
    const timer = window.setInterval(() => {
      load().catch(() => setPollError(true));
    }, 3000);
    return () => window.clearInterval(timer);
  }, [hasActiveJob, load]);

  const updateForm = (issueId, patch) => {
    setForms((current) => ({ ...current, [issueId]: { ...current[issueId], ...patch } }));
    setPreviews((current) => ({ ...current, [issueId]: null }));
  };
  const payloadFor = (issue) => ({ issue_id: issue.id, ...forms[issue.id] });
  const preview = async (issue) => {
    setBusyId(issue.id);
    try {
      const response = await api.post(`/import/${batchId}/seller-mappings/preview`, payloadFor(issue));
      setPreviews((current) => ({ ...current, [issue.id]: response.data }));
    } catch (error) { alert(error.response?.data?.detail || "Etki önizlemesi alınamadı"); }
    finally { setBusyId(null); }
  };
  const resolve = async (issue) => {
    const impact = previews[issue.id];
    if (!impact) return;
    const form = forms[issue.id];
    if (form.apply_historical && !window.confirm(
      `${impact.historical_impact.row_count} geçmiş kayıt güncellenecek. Net prim ve komisyon tutarları değişmeyecek. Onaylıyor musunuz?`
    )) return;
    setBusyId(issue.id);
    try {
      await api.post(`/import/${batchId}/seller-mappings/resolve`, {
        ...payloadFor(issue), preview_token: impact.preview_token,
      });
      await load();
    } catch (error) { alert(error.response?.data?.detail || "Eşleştirme uygulanamadı"); }
    finally { setBusyId(null); }
  };
  const retry = async (issue) => {
    setBusyId(issue.id);
    try {
      await api.post(`/import/${batchId}/seller-mappings/${issue.id}/retry`);
      await load();
    } catch (error) { alert(error.response?.data?.detail || "İşlem yeniden başlatılamadı"); }
    finally { setBusyId(null); }
  };
  const resume = async () => {
    setBusyId("resume");
    try {
      const response = await api.post(`/import/${batchId}/seller-mappings/resume`);
      onComplete(response.data);
    } catch (error) { alert(error.response?.data?.detail || "Taslak aktarım tamamlanamadı"); }
    finally { setBusyId(null); }
  };

  if (!data) return <div className="loading" data-testid="import-mapping-loading">Eşleştirmeler hazırlanıyor...</div>;
  return <section className="import-mapping-workbench" data-testid="import-mapping-workbench">
    <div className="import-mapping-head">
      <div><AlertTriangle size={18}/><div><strong>{data.unresolved_groups > 0 ? "Organizasyon eşleştirmesi gerekli" : "Eşleştirmeler tamamlandı"}</strong>
        <span>Aktarım taslakta. Mevcut aktif üretim etkilenmiyor.</span></div></div>
      <div className="mapping-count" data-testid="import-mapping-unresolved-count">
        <strong>{data.unresolved_groups}</strong><span>bekleyen grup · {data.unresolved_rows} kayıt</span>
      </div>
    </div>
    {data.issues.map((issue) => {
      const form = forms[issue.id] || emptyForm(issue);
      const impact = previews[issue.id];
      return <article className="mapping-issue" key={issue.id} data-testid={`mapping-issue-${issue.id}`}>
        <div className="mapping-issue-summary">
          <div><strong data-testid={`mapping-issue-name-${issue.id}`}>{issue.raw_name || `Satıcı adı boş · Satır ${issue.sample_rows?.[0]}`}</strong>
            <span className={issue.resolution ? "mapping-issue-status-resolved" : undefined}>
              {issue.resolution ? "Eşleştirme tamamlandı" : issue.reason_label}
            </span></div>
          <dl><div><dt>Kayıt</dt><dd>{issue.row_count}</dd></div><div><dt>Net prim</dt><dd>{money(issue.net_premium, true)}</dd></div>
            <div><dt>Komisyon</dt><dd>{money(issue.commission, true)}</dd></div></dl>
        </div>
        {issue.job && ["QUEUED", "RUNNING", "FAILED", "INTERRUPTED"].includes(issue.job.status) ? <div className={`mapping-job mapping-job-${issue.job.status.toLowerCase()}`} role="status" aria-live="polite" data-testid={`mapping-job-${issue.id}`}>
          <div className="mapping-job-heading"><strong>{issue.job.status === "FAILED" || issue.job.status === "INTERRUPTED" ? "Arka plan işlemi tamamlanmadı" : "Geçmiş kayıt eşleştirmesi sürüyor"}</strong>
            <span>{issue.job.message} {(issue.job.started_at || issue.job.created_at) && `· ${Math.max(0, Math.floor((nowMs - new Date(issue.job.started_at || issue.job.created_at).getTime()) / 1000))} sn`}</span></div>
          <div className="mapping-progress-track" role="progressbar" aria-label="Eşleştirme ilerlemesi" aria-valuemin="0" aria-valuemax="100" aria-valuenow={issue.job.progress ?? 5}>
            <div className="mapping-progress-value" style={{ width: `${issue.job.progress ?? 5}%` }}/>
          </div>
          <span className="mapping-job-stage">Aşama ilerlemesi: %{issue.job.progress ?? 5} · İşlenen kayıt yüzdesi değildir.</span>
          {pollError && <span role="alert">Durum bilgisi yenilenemiyor. Bağlantı kurulduğunda işlem sonucu tekrar kontrol edilecek.</span>}
          {["FAILED", "INTERRUPTED"].includes(issue.job.status) && <button type="button" className="outline-button" disabled={busyId === issue.id} onClick={() => retry(issue)}>Yeniden dene</button>}
        </div> : issue.resolution ? <div className="mapping-resolved" data-testid={`mapping-resolved-${issue.id}`}>
          <CheckCircle2 size={16}/><div><strong>{issue.resolution.node_name}</strong><span>{issue.resolution.path}</span></div>
        </div> : <>
          <div className="mapping-mode" data-testid={`mapping-mode-${issue.id}`}>
            <button type="button" data-testid={`mapping-existing-${issue.id}`} disabled={hasActiveJob || Boolean(busyId)} className={form.action === "LINK_EXISTING" ? "selected" : ""}
              onClick={() => updateForm(issue.id, { action: "LINK_EXISTING" })}><Link2 size={14}/> Mevcut satıcıya bağla</button>
            <button type="button" data-testid={`mapping-create-${issue.id}`} disabled={hasActiveJob || Boolean(busyId)} className={form.action === "CREATE_SELLER" ? "selected" : ""}
              onClick={() => updateForm(issue.id, { action: "CREATE_SELLER" })}><Plus size={14}/> Yeni satıcı oluştur</button>
          </div>
          {form.action === "LINK_EXISTING" ? <label>Organizasyon yolu
            <select data-testid={`mapping-node-select-${issue.id}`} value={form.node_id} onChange={(event) => updateForm(issue.id, { node_id: event.target.value })}>
              <option value="">Satıcı seçin</option>{options.sellers.map((seller) => <option key={seller.id} value={seller.id}>{seller.path}</option>)}
            </select></label> : <div className="mapping-create-grid">
            <label>Yeni satıcı adı<input data-testid={`mapping-new-name-${issue.id}`} value={form.seller_name} onChange={(event) => updateForm(issue.id, { seller_name: event.target.value })}/></label>
            <label>Bölge / alt yapı<select data-testid={`mapping-parent-select-${issue.id}`} value={form.parent_node_id} onChange={(event) => updateForm(issue.id, { parent_node_id: event.target.value })}>
              <option value="">Tam yolu seçin</option>{options.parents.map((parent) => <option key={parent.id} value={parent.id}>{parent.path}</option>)}
            </select></label>
          </div>}
          {busyId === issue.id && <div role="status">Eşleştirme ve etki bilgisi hazırlanıyor · {Math.max(0, Math.floor((nowMs - busyStartedAt) / 1000))} sn</div>}
          <button type="button" data-testid={`mapping-preview-${issue.id}`} className="outline-button" disabled={hasActiveJob || Boolean(busyId)}
            onClick={() => preview(issue)}><RefreshCw size={14}/> Etkiyi önizle</button>
          {impact && <div className="mapping-impact" data-testid={`mapping-impact-${issue.id}`}>
            <div><span>Seçilen yol</span><strong>{impact.target.path}</strong></div>
            <div><span>Geçmiş eşleşmemiş kayıt</span><strong>{impact.historical_impact.row_count} · {money(impact.historical_impact.net_premium, true)} · {money(impact.historical_impact.commission, true)}</strong></div>
            {impact.historical_impact.available && <label className="mapping-history-choice"><input type="checkbox" data-testid={`mapping-history-${issue.id}`}
              checked={form.apply_historical} onChange={(event) => setForms((current) => ({ ...current, [issue.id]: { ...form, apply_historical: event.target.checked } }))}/>
              Geçmiş eşleşmemiş kayıtlara da uygula</label>}
            <button type="button" data-testid={`mapping-confirm-${issue.id}`} className="primary-button" disabled={hasActiveJob || Boolean(busyId)}
              onClick={() => resolve(issue)}><CheckCircle2 size={14}/> Onayla ve bağla</button>
          </div>}
        </>}
      </article>;
    })}
    {busyId === "resume" && <div className="mapping-job" role="status" aria-live="polite">Dosya doğrulanıyor ve aktarım taslağı hazırlanıyor · {Math.max(0, Math.floor((nowMs - busyStartedAt) / 1000))} sn</div>}
    {data.unresolved_groups === 0 && <button type="button" data-testid="mapping-resume-import-button" className="primary-button mapping-resume"
      disabled={busyId === "resume"} onClick={resume}><CheckCircle2 size={15}/> Taslak aktarımı tamamla</button>}
  </section>;
};
